import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import TextField from '@material-ui/core/TextField';
import { useState } from 'react';

/**
 * GitHubRepo_TagField
 *
 * A per-ITEM custom field for the catalog `tags` array used by the Standardized
 * GitHub Repository Creation template. It is applied via `items.ui:field`, so
 * the default RJSF array widget (the add / remove / reorder chrome) is kept
 * unchanged — only each individual tag input is rendered by this component so
 * it can show a short, specific error message inline AS THE USER TYPES,
 * mirroring the repository-name field's live feedback.
 *
 * Blocking of "Next" is left to the schema (`pattern`, `minLength`/`maxLength`
 * on the item and `maxItems`/`uniqueItems` on the array): this component owns
 * the live message display, the schema owns validity. Because the item is
 * rendered by a custom field, RJSF does not also render the raw schema error, so
 * there is no duplicate/verbose message.
 *
 * The stored value is the same per-item `string` the default widget produced, so
 * the downstream Repo Foundry payload (`repository.tags`) is unchanged.
 */

const MAX_TAG_LENGTH = 100;
// Allowed characters: letters, numbers, hyphen, underscore, period.
const ALLOWED_TAG = /^[A-Za-z0-9._-]+$/;
// Two or more consecutive special characters (e.g. --, __, .., -_).
const CONSECUTIVE_SPECIALS = /[._-]{2}/;

/**
 * Validate a single tag against the naming/format rules and return a short,
 * specific message for the first violation found, or `undefined` when valid.
 * The order mirrors the repository-name validator so the messages are
 * consistent across the template.
 */
export function validateTag(tag: string): string | undefined {
  if (!tag) return 'Tag must not be empty.';
  if (tag.length > MAX_TAG_LENGTH) {
    return `Tag must be between 1 and ${MAX_TAG_LENGTH} characters.`;
  }
  if (/\s/.test(tag)) return 'Tag must not contain spaces.';
  if (!ALLOWED_TAG.test(tag)) {
    return 'Tag may only contain letters (a-z, A-Z), numbers (0-9), hyphen (-), underscore (_) and period (.).';
  }
  if (tag.startsWith('.')) return 'Tag must not start with a period (.).';
  if (tag.endsWith('.')) return 'Tag must not end with a period (.).';
  if (tag.startsWith('-') || tag.endsWith('-')) {
    return 'Tag must not start or end with a hyphen (-).';
  }
  if (CONSECUTIVE_SPECIALS.test(tag)) {
    return 'Tag must not contain multiple consecutive special characters (e.g. my--tag, my__tag, my..tag).';
  }
  return undefined;
}

/** Parse the zero-based array index out of an RJSF id like `root_tags_2`. */
function indexFromId(id?: string): number {
  const last = (id ?? '').split('_').pop();
  const n = Number(last);
  return Number.isInteger(n) ? n : -1;
}

export const TagField = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, formContext, schema, idSchema, required } = props;

  const value = formData ?? '';
  const [touched, setTouched] = useState(false);

  // Live duplicate detection against the sibling tags (the array-level
  // `uniqueItems` rule is what actually blocks "Next"; this only mirrors it
  // inline so the offending row is flagged as the user types).
  const tags =
    (formContext?.formData as { tags?: string[] } | undefined)?.tags ?? [];
  const index = indexFromId(idSchema?.$id);
  const isDuplicate =
    Boolean(value) &&
    index > 0 &&
    tags.findIndex((t, i) => t === value && i < index) !== -1;

  const error =
    validateTag(value) ??
    (isDuplicate ? 'Duplicate tag — tags must be unique.' : undefined);

  // Only surface the message once the user has interacted with (or typed into)
  // the row, so a freshly added empty row is not immediately marked invalid.
  const showError = Boolean(error) && (touched || value.length > 0);

  return (
    <TextField
      id={idSchema?.$id}
      label={schema?.title ?? 'Tag'}
      required={required}
      value={value}
      onChange={e => onChange(e.target.value)}
      onBlur={() => setTouched(true)}
      error={showError}
      helperText={showError ? error : undefined}
      margin="normal"
      fullWidth
    />
  );
};
